/**
 * Property / Project detail field visibility.
 *
 * Enabled-by-default (most fields):
 * - missing / empty show list → enabled for every category
 * - show list with only stale IDs (none match live categories) → treat as enabled for all
 * - otherwise → enabled only when categoryId is in the list
 *
 * Opt-in fields (Address, Address Details / Landmark):
 * - missing / empty show list → disabled for every category
 * - otherwise → enabled only when categoryId is in the list
 */

const OPT_IN_DETAIL_FIELDS = new Set(['address', 'address_details']);

export const isOptInDetailField = (field) => {
  const key = typeof field === 'string' ? field : field?.field;
  return OPT_IN_DETAIL_FIELDS.has(String(key || ''));
};

export const resolveCategoryEntityId = (item) => {
  if (!item || typeof item !== 'object') return null;
  const raw = item.category_id ?? item.id ?? item.categoryId;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

export const buildKnownCategoryIdSet = (categories = []) => {
  const set = new Set();
  (Array.isArray(categories) ? categories : []).forEach((c) => {
    const id = resolveCategoryEntityId(c);
    if (id != null) set.add(id);
  });
  return set;
};

/**
 * @returns {number[]|null} null means "enabled for all categories" (default)
 */
export const normalizeShowIds = (ids, knownCategoryIds) => {
  if (!Array.isArray(ids) || ids.length === 0) return null;
  const nums = ids.map(Number).filter((n) => Number.isFinite(n));
  if (!nums.length) return null;

  if (knownCategoryIds && knownCategoryIds.size > 0) {
    const valid = nums.filter((n) => knownCategoryIds.has(n));
    // Stale legacy IDs (e.g. 1–6) that don't exist in live data → default all on
    if (!valid.length) return null;
    return valid;
  }
  return nums;
};

export const getRawShowList = (field, { isProject = false } = {}) => {
  if (!field) return null;
  if (isProject) {
    if (Array.isArray(field.project_detail_show) && field.project_detail_show.length) {
      return field.project_detail_show;
    }
    if (Array.isArray(field.detail_show) && field.detail_show.length) {
      return field.detail_show;
    }
    return Array.isArray(field.project_detail_show) ? field.project_detail_show : null;
  }
  return Array.isArray(field.detail_show) ? field.detail_show : null;
};

export const isDetailFieldVisible = (
  field,
  categoryId,
  { isProject = false, knownCategoryIds } = {}
) => {
  const cat = Number(categoryId);
  if (!Number.isFinite(cat)) return false;

  const optIn = isOptInDetailField(field);
  const raw = getRawShowList(field, { isProject });

  // Opt-in: empty / missing → off. Default-on fields: empty / missing → on.
  if (!Array.isArray(raw) || raw.length === 0) return !optIn;

  const normalized = normalizeShowIds(raw, knownCategoryIds);

  // Stale IDs only → same as empty
  if (normalized == null) return !optIn;
  return normalized.includes(cat);
};

export const getRawLandTypeShowList = (field, { isProject = false } = {}) => {
  if (!field) return null;
  if (isProject) {
    if (Array.isArray(field.project_land_type_show) && field.project_land_type_show.length) {
      return field.project_land_type_show;
    }
    if (Array.isArray(field.land_type_show) && field.land_type_show.length) {
      return field.land_type_show;
    }
    return Array.isArray(field.project_land_type_show) ? field.project_land_type_show : null;
  }
  return Array.isArray(field.land_type_show) ? field.land_type_show : null;
};

export const isLandTypeDropdownVisible = (
  field,
  categoryId,
  { isProject = false, knownCategoryIds } = {}
) => {
  const cat = Number(categoryId);
  if (!Number.isFinite(cat)) return false;

  const normalized = normalizeShowIds(
    getRawLandTypeShowList(field, { isProject }),
    knownCategoryIds
  );

  // Default: dropdown enabled (matches current always-on behavior)
  if (normalized == null) return true;
  return normalized.includes(cat);
};

/**
 * Compute next show-id list after toggling one category.
 * Default-on fields: empty [] means enabled for all.
 * Opt-in fields: empty [] means enabled for none; all-on stores the full id list.
 */
export const toggleCategoryInShowList = (
  currentIds,
  categoryId,
  allCategoryIds,
  currentlyEnabled,
  { optIn = false } = {}
) => {
  const cat = Number(categoryId);
  const all = (allCategoryIds || []).map(Number).filter((n) => Number.isFinite(n));
  const known = new Set(all);
  const hasIds = Array.isArray(currentIds) && currentIds.length > 0;
  const normalized = hasIds ? normalizeShowIds(currentIds, known) : null;

  let enabledSet;
  if (!hasIds || normalized == null) {
    enabledSet = new Set(optIn ? [] : all);
  } else {
    enabledSet = new Set(normalized);
  }

  if (currentlyEnabled) enabledSet.delete(cat);
  else enabledSet.add(cat);

  if (enabledSet.size === 0) return [];
  // Default-on: all selected → []. Opt-in: keep explicit id list.
  if (!optIn && all.length && all.every((id) => enabledSet.has(id))) return [];
  return all.filter((id) => enabledSet.has(id));
};
